import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import {
  asLayout,
  localDay,
  type AnnouncementAction,
  type AnnouncementContent,
  type AnnouncementLayout,
} from "@/lib/announcements";

export const ANNOUNCEMENTS_CHANGED_EVENT = "flow:announcements-changed";

type RecordedEvent = "shown" | "closed" | "dismissed" | "completed" | "cta" | "news";

export async function recordAnnouncementEvent(id: string, event: RecordedEvent) {
  const { error } = await supabase.rpc("record_announcement_event", {
    p_announcement_id: id,
    p_event: event,
    p_today: localDay(),
  });
  if (error) console.error("record_announcement_event failed", error);
  window.dispatchEvent(new Event(ANNOUNCEMENTS_CHANGED_EVENT));
}

export function useAnnouncementActions(options: { record: boolean }) {
  const navigate = useNavigate();
  const [current, setCurrent] = useState<AnnouncementContent | null>(null);
  const { record } = options;

  const handleAction = useCallback(
    (action: AnnouncementAction) => {
      if (!current) return;
      const id = current.id;
      const track = (event: RecordedEvent) => {
        if (record) void recordAnnouncementEvent(id, event);
      };
      switch (action.action) {
        case "open":
          window.open(action.href, "_blank", "noopener,noreferrer");
          return;
        case "close":
          track("closed");
          setCurrent(null);
          return;
        case "dismiss":
          track("dismissed");
          setCurrent(null);
          return;
        case "complete":
          track("completed");
          setCurrent(null);
          return;
        case "navigate":
          setCurrent(null);
          navigate(action.href);
          return;
        case "cta":
          track("cta");
          setCurrent(null);
          if (action.href?.startsWith("/")) navigate(action.href);
          else if (action.href) window.open(action.href, "_blank", "noopener,noreferrer");
          return;
      }
    },
    [current, navigate, record],
  );

  return { current, setCurrent, handleAction };
}

export function useTodayAnnouncement() {
  const { user, activeWorkspaceId } = useAuth();
  const actions = useAnnouncementActions({ record: true });
  const { setCurrent } = actions;

  useEffect(() => {
    if (!user?.id || !activeWorkspaceId) return;
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.rpc("get_today_announcement", { p_today: localDay() });
      if (cancelled) return;
      if (error) {
        console.error("get_today_announcement failed", error);
        return;
      }
      const row = data?.[0];
      if (!row) return;
      setCurrent({ id: row.id, title: row.title, layout: asLayout(row.layout), html: row.html });
      void recordAnnouncementEvent(row.id, "shown");
    })();
    return () => {
      cancelled = true;
    };
  }, [user?.id, activeWorkspaceId, setCurrent]);

  return actions;
}

export type NewsState = "new" | "seen" | "dismissed" | "completed";

export interface NewsItem {
  id: string;
  title: string;
  summary: string | null;
  layout: AnnouncementLayout;
  announcedAt: string;
  state: NewsState;
}

function asNewsState(value: string): NewsState {
  return value === "seen" || value === "dismissed" || value === "completed" ? value : "new";
}

export function useAnnouncementNews() {
  const { user, activeWorkspaceId } = useAuth();
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id || !activeWorkspaceId) return;
    const { data, error: rpcError } = await supabase.rpc("list_announcement_news");
    if (rpcError) {
      setError("No pudimos cargar las noticias.");
      setLoading(false);
      return;
    }
    setError(null);
    setItems(
      (data ?? []).map((r) => ({
        id: r.id,
        title: r.title,
        summary: r.summary,
        layout: asLayout(r.layout),
        announcedAt: r.announced_at,
        state: asNewsState(r.state),
      })),
    );
    setLoading(false);
  }, [user?.id, activeWorkspaceId]);

  useEffect(() => {
    void load();
    const onChange = () => void load();
    window.addEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onChange);
    return () => window.removeEventListener(ANNOUNCEMENTS_CHANGED_EVENT, onChange);
  }, [load]);

  return { items, loading, error, reload: load };
}

export async function fetchAnnouncement(id: string): Promise<AnnouncementContent | null> {
  const { data, error } = await supabase.rpc("get_announcement", { p_announcement_id: id });
  if (error) throw error;
  const row = data?.[0];
  return row ? { id: row.id, title: row.title, layout: asLayout(row.layout), html: row.html } : null;
}
